import React, { memo, ReactElement, useCallback, useRef, useState } from "react";
import styles from "./TipInputBox.scss";
import { ENTER_TIP_AMOUNT, ASCII_CODES } from "../../constants";
import { InputFieldv4 } from "@swiggy-private/core-ui";
import { TIP_DETAIL_TEST_IDS } from "@swiggy-private/cart-test-ids";
import { TipWarnMessage } from "@swiggy-private/cart-types";
import { getErrorInfo } from "../../helpers";

export interface TipInputBoxProps {
    amount?: string;
    onUpdateTipAmount: (amount?: string) => void;
    onClearTipAmount: () => void;
    onInputFocus?: VoidFunction;
    onInputBlur?: VoidFunction;
    scrollIntoView?: boolean | number;
    showError?: boolean;
    errorMessage?: string;
    onInputChange?: (amount?: string) => void;
    maxValidTipAmount?: number;
    minValidTipAmount?: number;
    tipWarnMessage?: TipWarnMessage;
}

let timeoutId: number;
const DEBOUNCE_TIMEOUT = 700;

const TipInputBox = ({
    amount,
    onUpdateTipAmount,
    onInputFocus,
    onInputBlur,
    showError,
    errorMessage,
    onInputChange, //reflectChangedAmountImmediately has been removed since it was undefined at all times
    maxValidTipAmount,
    minValidTipAmount,
    tipWarnMessage,
}: TipInputBoxProps): ReactElement => {
    const [_amount, setAmount] = useState(amount || "");
    const inputFieldRef = useRef<HTMLInputElement>(null);
    const inputAttrs = { placeholder: ENTER_TIP_AMOUNT, maxlength: "5", type: "tel" };

    const onAmountChange = useCallback(
        (amount: string) => {
            if (!amount.match(/^[0-9]*$/)) return;
            setAmount(amount);
            if (typeof onInputChange === "function") {
                clearTimeout(timeoutId);
                timeoutId = window.setTimeout(() => {
                    onInputChange?.(amount);
                    // onUpdateTipAmount(amount) removed as it was dependent on reflectChangedAmountImmediately which in turn is always undefined
                }, DEBOUNCE_TIMEOUT);
            }
        },
        [onInputChange],
    );

    const handleKeyEvent = useCallback((event: React.KeyboardEvent<HTMLInputElement>) => {
        const { keyCode } = event;
        const preventDefaultAction =
            keyCode !== ASCII_CODES.BACK_SPACE &&
            keyCode !== ASCII_CODES.LEFT_ARROW &&
            keyCode !== ASCII_CODES.RIGHT_ARROW &&
            (keyCode < ASCII_CODES.ZERO || keyCode > ASCII_CODES.NINE);
        if (preventDefaultAction) {
            event.preventDefault();
        }

        // on enter call the api
        if (keyCode === ASCII_CODES.ENTER) {
            if (inputFieldRef && inputFieldRef.current != null) {
                inputFieldRef.current.blur();
            }
        }
    }, []);

    const handleInputBlur = useCallback(() => {
        // if condition removed as reflectChangedAmountImmediately is always undefined hence the if condition becomes always true
        onUpdateTipAmount(_amount);

        const errorState = getErrorInfo(Number(_amount), tipWarnMessage, minValidTipAmount, maxValidTipAmount);

        if (errorState?.show && Number(amount) >= 0) {
            amount && setAmount(amount || "");
            onAmountChange(String(amount));
        }

        if (typeof onInputBlur === "function") {
            onInputBlur();
        }
    }, [
        onUpdateTipAmount,
        onInputBlur,
        _amount,
        minValidTipAmount,
        maxValidTipAmount,
        tipWarnMessage,
        amount,
        onAmountChange,
    ]);

    return (
        <div className={styles.inputBoxWrapper}>
            <div className={styles.inputBoxPrefix} />
            <InputFieldv4
                ref={inputFieldRef}
                className={styles.inputBoxField}
                value={_amount}
                inputClassName={styles.inputBox}
                onChange={onAmountChange}
                onDown={handleKeyEvent}
                attrs={inputAttrs}
                onBlur={handleInputBlur}
                onFocus={onInputFocus}
                data-testid={TIP_DETAIL_TEST_IDS.DE_TIP_ENTER_TIP_AMOUNT}
                error={showError}
                errorMessage={errorMessage}
                showFocusBar={true}
            />
        </div>
    );
};

export default memo(TipInputBox);
